/**
 * BudgetTab — Management sub-tab for project budget & cost tracking.
 *
 * Three sections:
 *   1. Resource Costs — rollup of PreflightItem cost fields by phase
 *   2. Funding Sources — CRUD list of grants, donations, in-kind contributions
 *   3. Cost Tracking  — budget vs. actual progress bar (future: time-series chart)
 *
 * Epic: Budget & Cost Tracking (DEV-578 / DEV-635)
 */

import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { preflightStore, storeKey } from "../../Data/store";
import { usePreflightLoader } from "../../hooks/usePreflightLoader";
import type { PreflightItem, ResourcePhase, ResourceCategory } from "../../Data/PreFlight/preflightTypes";
import { RESOURCE_PHASES, RESOURCE_CATEGORIES, normalizeCategory, normalizePhase } from "../../Data/PreFlight/preflightTypes";
import {
  fetchFundingSources,
  fetchFundingSummary,
  createFundingSource,
  updateFundingSource,
  deleteFundingSource,
  apiResponseToFundingSource,
  FUNDING_SOURCE_TYPES,
  FUNDING_STATUSES,
} from "../../api/fundingSources";
import type {
  FundingSource,
  FundingSourceType,
  FundingStatus,
  FundingSummaryResponse,
  UpsertFundingSourceRequest,
} from "../../api/fundingSources";
import { formatCurrency } from "../../Data/Budget/currencyLookup";
import FundingSourceModal from "./FundingSourceModal";
import EvmDashboard from "./EvmDashboard";
import styles from "./BudgetTab.module.css";

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface Props {
  projectGuid: string;
  projectName: string;
  /** ISO 4217 currency code from project settings. */
  budgetCurrency: string;
  /** Contingency percentage (0–100). Defaults to 10. */
  contingencyPercent?: number;
  /** Callback to persist contingency % change to the project. */
  onContingencyChange?: (pct: number) => Promise<void>;
  canEdit: boolean;
  debugMode?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/** Map funding status → CSS class name */
function statusClass(status: string): string {
  switch (status) {
    case "pending":   return styles.statusPending;
    case "confirmed": return styles.statusConfirmed;
    case "received":  return styles.statusReceived;
    case "declined":  return styles.statusDeclined;
    case "withdrawn": return styles.statusWithdrawn;
    default:          return styles.statusPending;
  }
}

/** Phase label (localised) */
function phaseLabel(phase: ResourcePhase, t: (key: string) => string): string {
  const map: Record<ResourcePhase, string> = {
    preparation: t("preflight.phases.preparation"),
    operational: t("preflight.phases.operational"),
    sustainment: t("preflight.phases.sustainment"),
  };
  return map[phase] ?? phase;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const BudgetTab: React.FC<Props> = ({
  projectGuid,
  projectName,
  budgetCurrency,
  contingencyPercent = 10,
  onContingencyChange,
  canEdit,
  debugMode,
}) => {
  const { t: tPages } = useAppTranslation("pages");
  const { t: tKpi } = useAppTranslation("kpi");
  const { t: tCommon } = useAppTranslation("common");

  /* ── Contingency inline editor ── */
  const [editingContingency, setEditingContingency] = useState(false);
  const [contingencyDraft, setContingencyDraft] = useState(contingencyPercent);
  const [contingencySaving, setContingencySaving] = useState(false);
  // Optimistic value: used between save and prop refresh so the display
  // doesn't flash back to the old value while the async refetch is in-flight.
  const [optimisticContingency, setOptimisticContingency] = useState<number | null>(null);
  // Bumped after contingency save so the EVM dashboard re-fetches with
  // the updated BAC (which includes contingency in the backend calc).
  const [evmRefreshBump, setEvmRefreshBump] = useState(0);

  // Effective contingency: optimistic override → prop
  const effectiveContingency = optimisticContingency ?? contingencyPercent;

  // Clear the optimistic value once the prop catches up
  useEffect(() => {
    if (optimisticContingency !== null && contingencyPercent === optimisticContingency) {
      setOptimisticContingency(null);
    }
  }, [contingencyPercent, optimisticContingency]);

  const handleContingencyEdit = useCallback(() => {
    setContingencyDraft(effectiveContingency);
    setEditingContingency(true);
  }, [effectiveContingency]);

  const handleContingencySave = useCallback(async () => {
    if (!onContingencyChange) return;
    const clamped = Math.max(0, Math.min(100, contingencyDraft));
    setContingencySaving(true);
    try {
      await onContingencyChange(clamped);
      setOptimisticContingency(clamped);
      setEditingContingency(false);
      // Trigger EVM dashboard re-fetch so EAC/BAC/VAC reflect new contingency
      setEvmRefreshBump((n) => n + 1);
    } catch (err: any) {
      console.error("[BudgetTab] Failed to save contingency:", err.message);
    } finally {
      setContingencySaving(false);
    }
  }, [onContingencyChange, contingencyDraft]);

  const handleContingencyCancel = useCallback(() => {
    setEditingContingency(false);
    setContingencyDraft(effectiveContingency);
  }, [effectiveContingency]);

  /* ── Pre-flight data (BOM) ── */
  const { preflightVersion } = usePreflightLoader(projectGuid, true);

  const preflightItems: PreflightItem[] = useMemo(() => {
    void preflightVersion;
    if (!projectGuid) return [];
    return preflightStore.get(storeKey(projectGuid)) || [];
  }, [projectGuid, preflightVersion]);

  /* ── BOM cost rollup ── */
  const bomSummary = useMemo(() => {
    const byPhase: Record<ResourcePhase, { estimated: number; actual: number; count: number }> = {
      preparation: { estimated: 0, actual: 0, count: 0 },
      operational: { estimated: 0, actual: 0, count: 0 },
      sustainment: { estimated: 0, actual: 0, count: 0 },
    };

    const byCategory: Record<string, { estimated: number; actual: number; count: number }> = {};
    for (const cc of RESOURCE_CATEGORIES) {
      byCategory[cc] = { estimated: 0, actual: 0, count: 0 };
    }

    let totalEstimated = 0;
    let totalActual = 0;

    for (const item of preflightItems) {
      if (item.state === "withdrawn") continue;
      const phase = byPhase[normalizePhase(item.phase)];
      if (!phase) continue;

      const est = item.estimated_total ?? (
        item.unit_cost != null && item.target_quantity != null
          ? item.unit_cost * item.target_quantity
          : 0
      );
      const act = item.actual_total ?? (
        item.actual_cost != null
          ? item.actual_cost * item.current_quantity
          : 0
      );

      phase.estimated += est;
      phase.actual += act;
      phase.count++;

      const cc = normalizeCategory(item.category);
      if (!byCategory[cc]) byCategory[cc] = { estimated: 0, actual: 0, count: 0 };
      byCategory[cc].estimated += est;
      byCategory[cc].actual += act;
      byCategory[cc].count++;

      totalEstimated += est;
      totalActual += act;
    }

    /* Phase × Category cross-tab */
    const crossTab: Record<string, Record<string, { estimated: number; actual: number; count: number }>> = {};
    for (const phase of RESOURCE_PHASES) {
      crossTab[phase] = {};
      for (const cc of RESOURCE_CATEGORIES) {
        crossTab[phase][cc] = { estimated: 0, actual: 0, count: 0 };
      }
    }
    for (const item of preflightItems) {
      if (item.state === "withdrawn") continue;
      const cc = normalizeCategory(item.category);
      const cell = crossTab[normalizePhase(item.phase)]?.[cc];
      if (!cell) continue;
      const est = item.estimated_total ?? (
        item.unit_cost != null && item.target_quantity != null
          ? item.unit_cost * item.target_quantity : 0
      );
      const act = item.actual_total ?? (
        item.actual_cost != null ? item.actual_cost * item.current_quantity : 0
      );
      cell.estimated += est;
      cell.actual += act;
      cell.count++;
    }

    const contingencyAmount = totalEstimated * (effectiveContingency / 100);
    const grandTotal = totalEstimated + contingencyAmount;

    return { byPhase, byCategory, crossTab, totalEstimated, totalActual, contingencyAmount, grandTotal };
  }, [preflightItems, effectiveContingency]);

  /* ── Funding sources ── */
  const [sources, setSources] = useState<FundingSource[]>([]);
  const [fundingSummary, setFundingSummary] = useState<FundingSummaryResponse | null>(null);
  const [fundingLoading, setFundingLoading] = useState(true);
  const [fundingError, setFundingError] = useState<string | null>(null);

  const loadFunding = useCallback(async () => {
    if (!projectGuid) return;
    setFundingLoading(true);
    setFundingError(null);
    try {
      const [srcResp, summResp] = await Promise.all([
        fetchFundingSources(projectGuid),
        fetchFundingSummary(projectGuid),
      ]);
      setSources(srcResp.map(apiResponseToFundingSource));
      setFundingSummary(summResp);
    } catch (err: any) {
      console.warn("[BudgetTab] Failed to load funding sources:", err.message);
      setFundingError(err.message);
    } finally {
      setFundingLoading(false);
    }
  }, [projectGuid]);

  useEffect(() => {
    loadFunding();
  }, [loadFunding]);

  /* ── Funding source modal ── */
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSource, setEditingSource] = useState<FundingSource | null>(null);

  const handleAddSource = useCallback(() => {
    setEditingSource(null);
    setModalOpen(true);
  }, []);

  const handleEditSource = useCallback((source: FundingSource) => {
    setEditingSource(source);
    setModalOpen(true);
  }, []);

  const handleSaveSource = useCallback(async (req: UpsertFundingSourceRequest) => {
    try {
      if (editingSource) {
        await updateFundingSource(projectGuid, editingSource.id, req);
      } else {
        await createFundingSource(projectGuid, req);
      }
      setModalOpen(false);
      setEditingSource(null);
      await loadFunding();
    } catch (err: any) {
      console.error("[BudgetTab] Save funding source failed:", err.message);
      throw err; // Let modal handle the error display
    }
  }, [projectGuid, editingSource, loadFunding]);

  const handleDeleteSource = useCallback(async (sourceId: string) => {
    try {
      await deleteFundingSource(projectGuid, sourceId);
      await loadFunding();
    } catch (err: any) {
      console.error("[BudgetTab] Delete funding source failed:", err.message);
    }
  }, [projectGuid, loadFunding]);

  /* ── Budget coverage ── */
  const budgetCoverage = useMemo(() => {
    if (!fundingSummary || bomSummary.grandTotal <= 0) return 0;
    return Math.min(100, Math.round((fundingSummary.totalPledged / bomSummary.grandTotal) * 100));
  }, [fundingSummary, bomSummary.grandTotal]);

  const costProgress = useMemo(() => {
    if (bomSummary.totalEstimated <= 0) return 0;
    return Math.min(100, Math.round((bomSummary.totalActual / bomSummary.totalEstimated) * 100));
  }, [bomSummary.totalEstimated, bomSummary.totalActual]);

  /* ── Render ── */
  return (
    <div className={styles.budgetTab}>
      {/* ── Section 1: Resource Costs ── */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>{tPages("budget.summaryTitle")}</h3>
        </div>
        <div className={styles.sectionBody}>
          {/* Summary cards */}
          <div className={styles.summaryRow}>
            <div className={styles.summaryCard}>
              <span className={styles.summaryLabel}>{tPages("budget.resourceCosts")}</span>
              <span className={styles.summaryValue}>
                {formatCurrency(bomSummary.totalEstimated, budgetCurrency)}
              </span>
              <span className={styles.summaryMeta}>
                {preflightItems.filter(i => i.state !== "withdrawn").length} {tKpi("preflight.title").toLowerCase()}
              </span>
            </div>
            <div className={styles.summaryCard}>
              <span className={styles.summaryLabel}>{tPages("budget.contingency")}</span>
              <span className={styles.summaryValue}>
                {formatCurrency(bomSummary.contingencyAmount, budgetCurrency)}
              </span>
              {editingContingency ? (
                <span className={styles.contingencyEditor}>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    value={contingencyDraft}
                    onChange={(e) => setContingencyDraft(Number(e.target.value))}
                    className={styles.contingencyInput}
                    disabled={contingencySaving}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleContingencySave();
                      if (e.key === "Escape") handleContingencyCancel();
                    }}
                  />
                  <span>%</span>
                  <button
                    className={styles.iconBtn}
                    onClick={handleContingencySave}
                    disabled={contingencySaving}
                    title={tCommon("save")}
                  >✓</button>
                  <button
                    className={styles.iconBtn}
                    onClick={handleContingencyCancel}
                    disabled={contingencySaving}
                    title={tCommon("cancel")}
                  >✕</button>
                </span>
              ) : (
                <span
                  className={styles.summaryMeta}
                  style={canEdit && onContingencyChange ? { cursor: "pointer", textDecoration: "underline dotted" } : undefined}
                  onClick={canEdit && onContingencyChange ? handleContingencyEdit : undefined}
                  title={canEdit && onContingencyChange ? tPages("budget.editContingency") : tPages("budget.contingencyHelp")}
                >
                  {effectiveContingency}%{canEdit && onContingencyChange ? " ✎" : ""}
                </span>
              )}
            </div>
            <div className={styles.summaryCard}>
              <span className={styles.summaryLabel}>{tPages("budget.totalBudget")}</span>
              <span className={styles.summaryValue}>
                {formatCurrency(bomSummary.grandTotal, budgetCurrency)}
              </span>
            </div>
            {fundingSummary && (
              <div className={styles.summaryCard}>
                <span className={styles.summaryLabel}>{tPages("budget.totalPledged")}</span>
                <span className={styles.summaryValue}>
                  {formatCurrency(fundingSummary.totalPledged, budgetCurrency)}
                </span>
                <span className={styles.summaryMeta}>
                  {budgetCoverage}% {tPages("budget.costTracking").toLowerCase()}
                </span>
              </div>
            )}
          </div>

          {/* Phase breakdown — sub-section bar + table */}
          <div className={styles.subSectionBar}>
            <span className={styles.subSectionIcon}>📊</span>
            <h4 className={styles.subSectionLabel}>{tKpi("preflight.phase")}</h4>
            <span className={styles.subSectionRight}>{tPages("budget.estimatedVsActual")}</span>
          </div>
          <table className={styles.phaseTable}>
            <tbody>
              {RESOURCE_PHASES.map((phase) => {
                const p = bomSummary.byPhase[phase];
                return (
                  <tr key={phase}>
                    <td>{phaseLabel(phase, tKpi)}</td>
                    <td style={{ textAlign: "end" as const }}>
                      {formatCurrency(p.estimated, budgetCurrency)}
                      {p.actual > 0 && (
                        <span style={{ color: "var(--text-muted)", marginInlineStart: "0.5rem" }}>
                          / {formatCurrency(p.actual, budgetCurrency)}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className={styles.contingencyRow}>
                <td>{tPages("budget.contingency")} ({effectiveContingency}%)</td>
                <td>{formatCurrency(bomSummary.contingencyAmount, budgetCurrency)}</td>
              </tr>
              <tr>
                <td>{tPages("budget.totalBudget")}</td>
                <td>{formatCurrency(bomSummary.grandTotal, budgetCurrency)}</td>
              </tr>
            </tfoot>
          </table>

          {/* Cost Category breakdown — sub-section bar + table */}
          <div className={styles.subSectionBar}>
            <span className={styles.subSectionIcon}>💰</span>
            <h4 className={styles.subSectionLabel}>{tKpi("preflight.costCategory")}</h4>
            <span className={styles.subSectionRight}>{tPages("budget.estimatedVsActual")}</span>
          </div>
          <table className={styles.phaseTable}>
            <tbody>
              {RESOURCE_CATEGORIES
                .filter((cc) => bomSummary.byCategory[cc]?.count > 0)
                .map((cc) => {
                  const c = bomSummary.byCategory[cc];
                  return (
                    <tr key={cc}>
                      <td>{tKpi(`preflight.costCategories.${cc}`)}</td>
                      <td style={{ textAlign: "end" as const }}>
                        {formatCurrency(c.estimated, budgetCurrency)}
                        {c.actual > 0 && (
                          <span style={{ color: "var(--text-muted)", marginInlineStart: "0.5rem" }}>
                            / {formatCurrency(c.actual, budgetCurrency)}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>

          {/* Phase × Category cross-tab matrix */}
          {(() => {
            const activeCats = RESOURCE_CATEGORIES.filter(cc => bomSummary.byCategory[cc]?.count > 0);
            if (activeCats.length === 0) return null;
            return (
              <div style={{ marginBlockStart: "0.25rem" }}>
                <div className={styles.subSectionBar}>
                  <span className={styles.subSectionIcon}>📐</span>
                  <h4 className={styles.subSectionLabel}>{tPages("budget.phaseByCategory")}</h4>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table className={styles.phaseTable}>
                    <thead>
                      <tr>
                        <th></th>
                        {activeCats.map(cc => (
                          <th key={cc} style={{ textAlign: "end" as const, whiteSpace: "normal", minWidth: "5.5rem" }}>
                            {tKpi(`preflight.costCategories.${cc}`).replace(/ & /g, "\u00A0& ")}
                          </th>
                        ))}
                        <th style={{ textAlign: "end" as const }}>{tPages("budget.totalBudget")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {RESOURCE_PHASES.map(phase => {
                        const phaseRow = bomSummary.crossTab[phase];
                        const phaseTotal = bomSummary.byPhase[phase].estimated;
                        return (
                          <tr key={phase}>
                            <td>{phaseLabel(phase, tKpi)}</td>
                            {activeCats.map(cc => {
                              const cell = phaseRow[cc];
                              return (
                                <td key={cc} style={{ textAlign: "end" as const }}>
                                  {cell.estimated > 0 ? formatCurrency(cell.estimated, budgetCurrency) : "—"}
                                </td>
                              );
                            })}
                            <td style={{ textAlign: "end" as const, fontWeight: 600 }}>
                              {formatCurrency(phaseTotal, budgetCurrency)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td>{tPages("budget.totalBudget")}</td>
                        {activeCats.map(cc => (
                          <td key={cc} style={{ textAlign: "end" as const, fontWeight: 600 }}>
                            {formatCurrency(bomSummary.byCategory[cc].estimated, budgetCurrency)}
                          </td>
                        ))}
                        <td style={{ textAlign: "end" as const, fontWeight: 700 }}>
                          {formatCurrency(bomSummary.totalEstimated, budgetCurrency)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* ── Section 2: Funding Sources ── */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>{tPages("budget.fundingSources")}</h3>
          {canEdit && (
            <button className={styles.addBtn} onClick={handleAddSource}>
              + {tPages("budget.addSource")}
            </button>
          )}
        </div>
        <div className={styles.sectionBody}>
          {fundingLoading ? (
            <div className={styles.loading}>{tCommon("loading")}…</div>
          ) : fundingError ? (
            <div className={styles.emptyState}>
              <span>{fundingError}</span>
            </div>
          ) : sources.length === 0 ? (
            <div className={styles.emptyState}>
              <span>{tPages("budget.fundingSources")} — {tCommon("empty")}</span>
              {canEdit && (
                <button className={styles.addBtn} onClick={handleAddSource}>
                  + {tPages("budget.addSource")}
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Summary row for funding */}
              {fundingSummary && (
                <div className={styles.summaryRow} style={{ marginBlockEnd: "1rem" }}>
                  <div className={styles.summaryCard}>
                    <span className={styles.summaryLabel}>{tPages("budget.totalPledged")}</span>
                    <span className={styles.summaryValue}>
                      {formatCurrency(fundingSummary.totalPledged, budgetCurrency)}
                    </span>
                    <span className={styles.summaryMeta}>
                      {fundingSummary.sourceCount} {tPages("budget.fundingSources").toLowerCase()}
                    </span>
                  </div>
                  <div className={styles.summaryCard}>
                    <span className={styles.summaryLabel}>{tPages("budget.totalReceived")}</span>
                    <span className={styles.summaryValue}>
                      {formatCurrency(fundingSummary.totalReceived, budgetCurrency)}
                    </span>
                    <span className={styles.summaryMeta}>
                      {fundingSummary.confirmedCount} {tPages("budget.statuses.confirmed").toLowerCase()}
                    </span>
                  </div>
                </div>
              )}

              {/* Funding sources table */}
              <table className={styles.fundingTable}>
                <thead>
                  <tr>
                    <th>{tPages("budget.sourceName")}</th>
                    <th>{tPages("budget.sourceType")}</th>
                    <th>{tPages("budget.amountPledged")}</th>
                    <th>{tPages("budget.amountReceived")}</th>
                    <th>{tPages("budget.status")}</th>
                    {canEdit && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {sources.map((src) => (
                    <tr key={src.id}>
                      <td>{src.sourceName}</td>
                      <td>{tPages(`budget.sourceTypes.${src.sourceType}`)}</td>
                      <td>{formatCurrency(src.amountPledged, budgetCurrency)}</td>
                      <td>{formatCurrency(src.amountReceived, budgetCurrency)}</td>
                      <td>
                        <span className={`${styles.statusBadge} ${statusClass(src.status)}`}>
                          {tPages(`budget.statuses.${src.status}`)}
                        </span>
                      </td>
                      {canEdit && (
                        <td>
                          <div className={styles.rowActions}>
                            <button
                              className={styles.iconBtn}
                              onClick={() => handleEditSource(src)}
                              title={tCommon("edit")}
                            >
                              ✎
                            </button>
                            <button
                              className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                              onClick={() => handleDeleteSource(src.id)}
                              title={tCommon("delete")}
                            >
                              ✕
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>

      {/* ── Section 3: Cost Tracking ── */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>{tPages("budget.costTracking")}</h3>
        </div>
        <div className={styles.sectionBody}>
          <div className={styles.summaryRow}>
            <div className={styles.summaryCard}>
              <span className={styles.summaryLabel}>{tPages("budget.estimatedVsActual")}</span>
              <span className={styles.summaryValue}>
                {formatCurrency(bomSummary.totalActual, budgetCurrency)}
                <span style={{ color: "var(--text-muted)", fontSize: "0.875rem", fontWeight: 400, marginInlineStart: "0.375rem" }}>
                  / {formatCurrency(bomSummary.totalEstimated, budgetCurrency)}
                </span>
              </span>
              <div className={styles.progressBar}>
                <div
                  className={`${styles.progressFill} ${
                    costProgress > 100 ? styles.progressRed :
                    costProgress > 80 ? styles.progressYellow :
                    styles.progressGreen
                  }`}
                  style={{ width: `${Math.min(costProgress, 100)}%` }}
                />
              </div>
              <span className={styles.summaryMeta}>{costProgress}%</span>
            </div>
            {fundingSummary && bomSummary.grandTotal > 0 && (
              <div className={styles.summaryCard}>
                <span className={styles.summaryLabel}>{tPages("budget.fundingSources")}</span>
                <span className={styles.summaryValue}>
                  {formatCurrency(fundingSummary.totalReceived, budgetCurrency)}
                  <span style={{ color: "var(--text-muted)", fontSize: "0.875rem", fontWeight: 400, marginInlineStart: "0.375rem" }}>
                    / {formatCurrency(bomSummary.grandTotal, budgetCurrency)}
                  </span>
                </span>
                <div className={styles.progressBar}>
                  <div
                    className={`${styles.progressFill} ${
                      budgetCoverage >= 100 ? styles.progressGreen :
                      budgetCoverage >= 50 ? styles.progressYellow :
                      styles.progressRed
                    }`}
                    style={{ width: `${budgetCoverage}%` }}
                  />
                </div>
                <span className={styles.summaryMeta}>{budgetCoverage}%</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── EVM-Lite Dashboard ── */}
      <div className={styles.section}>
        <EvmDashboard
          projectGuid={projectGuid}
          budgetCurrency={budgetCurrency}
          canEdit={canEdit}
          refreshTrigger={preflightVersion + evmRefreshBump}
          debugMode={debugMode}
        />
      </div>

      {/* Funding source add/edit modal */}
      {modalOpen && (
        <FundingSourceModal
          source={editingSource}
          budgetCurrency={budgetCurrency}
          onSave={handleSaveSource}
          onClose={() => { setModalOpen(false); setEditingSource(null); }}
        />
      )}
    </div>
  );
};

export default BudgetTab;
